/**
 * EngineOptimizerCard — surfaces a per-asset cost-saver recommendation
 * by comparing actual engine consumption against the cheapest projected
 * alternative. Backed by `/assets/{id}/engine-advice` which re-projects
 * each transaction's base credits across every engine multiplier.
 *
 * The card stays HIDDEN until the user has spent ≥30 base credits on a
 * non-cheapest engine — anything less is statistical noise and an
 * unwarranted nudge to switch engines.
 */
import { Sparkles, TrendingDown, ArrowRight } from 'lucide-react';

const TIER_LABEL = {
  'claude-opus-4-6': 'Pro 4.6',
  'claude-opus-4-7': 'Ultra 4.7',
  'claude-opus-4-8': 'Apex 4.8',
};
const prettyTier = (id) =>
  TIER_LABEL[id] || (id || '').replace('claude-', '').replace(/-/g, ' ');

export const EngineOptimizerCard = ({ advice, onSwitchEngine }) => {
  if (!advice || !advice.recommendation) return null;
  const rec = advice.recommendation;
  const fromName = prettyTier(rec.from);
  const toName = prettyTier(rec.to);

  return (
    <div
      className="rounded-xl p-4 mb-3"
      style={{
        background: 'linear-gradient(135deg, rgba(16,185,129,0.10), rgba(110,231,183,0.04))',
        border: '1px solid rgba(16,185,129,0.28)',
      }}
      data-testid="engine-optimizer-card"
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(16,185,129,0.18)', color: '#10B981' }}
        >
          <Sparkles size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <div
              className="text-[10px] font-semibold tracking-wider uppercase"
              style={{ color: '#10B981' }}
            >
              Engine Optimizer
            </div>
            <div
              className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded font-semibold"
              style={{ background: 'rgba(16,185,129,0.18)', color: '#10B981' }}
              data-testid="engine-optimizer-savings"
            >
              <TrendingDown size={10} />
              −{rec.savings_credits} cr · −{rec.savings_pct}%
            </div>
          </div>
          <div className="text-[13px] font-semibold text-nexus-text-primary leading-snug mb-1">
            Switch from <span style={{ color: '#F26B1F' }}>{fromName}</span>
            <ArrowRight size={12} className="inline mx-1.5 opacity-60" />
            <span style={{ color: '#10B981' }}>{toName}</span> for routine runs
          </div>
          <div className="text-[11.5px] text-nexus-text-secondary leading-relaxed">
            {rec.reason}
          </div>
          <div className="mt-2.5 text-[10.5px] text-nexus-text-tertiary font-mono">
            Current: {Math.round(rec.current_total).toLocaleString()} cr on {fromName}
            <span className="opacity-50 mx-1.5">·</span>
            Projected: {Math.round(rec.projected_total).toLocaleString()} cr on {toName}
            <span className="opacity-50 mx-1.5">·</span>
            Based on {advice.total_base_credits.toLocaleString()} base credits across {rec.calls_analysed} call{rec.calls_analysed === 1 ? '' : 's'}
          </div>
          {onSwitchEngine && (
            <button
              type="button"
              onClick={() => onSwitchEngine(rec.to)}
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all hover:scale-[1.02]"
              style={{
                background: 'rgba(16,185,129,0.18)',
                border: '1px solid rgba(16,185,129,0.45)',
                color: '#10B981',
              }}
              data-testid="engine-optimizer-switch-btn"
            >
              Switch to {toName} <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EngineOptimizerCard;
